import React, { useState, useEffect, useMemo } from "react";
import DataTable from "react-data-table-component";
import { AiOutlineEdit, AiOutlineCheckCircle, AiOutlineUser } from "react-icons/ai";
import contentService from "../../services/contentService";

// API Configuration
import { API_CONFIG } from '../../config/env';
const API_BASE_URL = API_CONFIG.BASE_URL;

export default function ContactApproaches() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [search, setSearch] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("all");
    const [selectedPriority, setSelectedPriority] = useState("all");
    
    // Modal states
    const [selectedContact, setSelectedContact] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [adminNotes, setAdminNotes] = useState("");
    const [isUpdating, setIsUpdating] = useState(false);
    
    // Success message state
    const [successMsg, setSuccessMsg] = useState("");

    // Status and Priority options
    const statusOptions = [
        { value: "all", label: "All Status" },
        { value: "pending", label: "Pending" },
        { value: "in_progress", label: "In Progress" },
        { value: "contacted", label: "Contacted" },
        { value: "resolved", label: "Resolved" },
        { value: "closed", label: "Closed" }
    ];

    const priorityOptions = [
        { value: "all", label: "All Priority" },
        { value: "low", label: "Low" },
        { value: "medium", label: "Medium" },
        { value: "high", label: "High" },
        { value: "urgent", label: "Urgent" }
    ];

    // Fetch data
    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await contentService.getContactMessages();
            let contactData = response.results || response;
            
            // Ensure all IDs are strings to prevent scientific notation issues
            if (Array.isArray(contactData)) {
                contactData = contactData.map(contact => ({
                    ...contact,
                    id: String(contact.id) // Ensure ID is always a string
                }));
            }
            
            setData(contactData);
        } catch (error) {
            console.error("Error fetching contact approaches:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Filter data based on search and filters
    const filteredData = useMemo(() => {
        return data.filter(contact => {
            const matchesSearch = 
                contact.name.toLowerCase().includes(search.toLowerCase()) ||
                contact.email.toLowerCase().includes(search.toLowerCase()) ||
                (contact.subject && contact.subject.toLowerCase().includes(search.toLowerCase())) ||
                contact.message.toLowerCase().includes(search.toLowerCase());
            
            const matchesStatus = selectedStatus === "all" || contact.status === selectedStatus;
            const matchesPriority = selectedPriority === "all" || contact.priority === selectedPriority;
            
            return matchesSearch && matchesStatus && matchesPriority;
        });
    }, [data, search, selectedStatus, selectedPriority]);

    // Table columns
    const columns = [
        {
            name: "Name",
            selector: row => row.name,
            sortable: true,
            cell: row => (
                <div>
                    <div className="fw-bold">{row.name}</div>
                    <small className="text-muted">{row.email}</small>
                </div>
            )
        },
        {
            name: "Subject",
            selector: row => row.subject || "No Subject",
            sortable: true,
            cell: row => (
                <div>
                    <div>{row.subject || "No Subject"}</div>
                    {row.phone && <small className="text-muted">{row.phone}</small>}
                </div>
            )
        },
        {
            name: "Status",
            selector: row => row.status,
            sortable: true,
            cell: row => (
                <span className={`badge ${
                    row.status === 'pending' ? 'bg-warning' :
                    row.status === 'in_progress' ? 'bg-info' :
                    row.status === 'contacted' ? 'bg-primary' :
                    row.status === 'resolved' ? 'bg-success' :
                    'bg-secondary'
                }`}>
                    {row.status.replace('_', ' ').toUpperCase()}
                </span>
            )
        },
        {
            name: "Priority",
            selector: row => row.priority,
            sortable: true,
            cell: row => (
                <span className={`badge ${
                    row.priority === 'low' ? 'bg-light text-dark' :
                    row.priority === 'medium' ? 'bg-warning' :
                    row.priority === 'high' ? 'bg-danger' :
                    'bg-dark'
                }`}>
                    {row.priority.toUpperCase()}
                </span>
            )
        },
        {
            name: "Assigned To",
            selector: row => row.assigned_to_name || "Unassigned",
            sortable: true,
            cell: row => (
                <div>
                    {row.assigned_to_name ? (
                        <span className="text-primary">{row.assigned_to_name}</span>
                    ) : (
                        <span className="text-muted">Unassigned</span>
                    )}
                </div>
            )
        },
        {
            name: "Date",
            selector: row => new Date(row.created_at).toLocaleDateString(),
            sortable: true,
            cell: row => (
                <div>
                    <div>{new Date(row.created_at).toLocaleDateString()}</div>
                    <small className="text-muted">{new Date(row.created_at).toLocaleTimeString()}</small>
                </div>
            )
        },
        {
            name: "Actions",
            cell: row => (
                <div className="d-flex gap-1">
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleViewDetails(row)}
                        title="View Details"
                    >
                        <AiOutlineEdit size={16} />
                    </button>
                    <button
                        className="btn btn-sm btn-outline-success"
                        onClick={() => handleAssignToMe(row)}
                        title="Assign to Me"
                    >
                        <AiOutlineUser size={16} />
                    </button>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        }
    ];

    // Handle view details
    const handleViewDetails = (contact) => {
        setSelectedContact(contact);
        setAdminNotes(contact.admin_notes || "");
        setShowDetailsModal(true);
    };

    // Handle assign to me
    const handleAssignToMe = async (contact) => {
        try {
            // Ensure ID is converted to string to prevent scientific notation issues
            const contactId = String(contact.id);
            await contentService.assignContactToMe(contactId);
            await fetchData(); // Refresh data
            setSuccessMsg("Contact assigned to you successfully!");
        } catch (error) {
            setSuccessMsg("Error assigning contact: " + error.message);
        }
    };


    // Handle update contact
    const handleUpdateContact = async () => {
        if (!selectedContact) return;
        
        setIsUpdating(true);
        try {
            // Ensure ID is converted to string to prevent scientific notation issues
            const contactId = String(selectedContact.id);
            await contentService.updateContactMessage(contactId, {
                status: selectedContact.status,
                priority: selectedContact.priority,
                admin_notes: adminNotes
            });
            await fetchData(); // Refresh data
            setShowDetailsModal(false);
            setSuccessMsg("Contact updated successfully!");
        } catch (error) {
            setSuccessMsg("Error updating contact: " + error.message);
        } finally {
            setIsUpdating(false);
        }
    };

    // Close success message
    const closeSuccess = () => {
        setSuccessMsg("");
    };

    // Modal button styles
    const modalBtn = (bg, color, size = 15, pad = 8) => ({
        background: bg,
        color: color,
        border: "none",
        borderRadius: 8,
        padding: `${pad}px ${pad * 1.5}px`,
        fontSize: size,
        fontWeight: 600,
        cursor: "pointer",
        userSelect: "none",
        transition: "all 0.2s ease"
    });

    // SuccessModal component
    function SuccessModal({ message, onClose }) {
        return (
            <div
                role="alertdialog"
                aria-modal="true"
                onClick={onClose}
                style={{
                    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: "rgba(0,0,0,0.13)", zIndex: 10002,
                    display: "flex", justifyContent: "center", alignItems: "center"
                }}>
                <div
                    onClick={e => e.stopPropagation()}
                    style={{
                        background: "#fafff9",
                        padding: "36px 30px 26px 30px",
                        borderRadius: 16,
                        textAlign: "center",
                        boxShadow: "0 8px 36px #53ec8860",
                        color: "#11602c"
                    }}>
                    <AiOutlineCheckCircle
                        size={62}
                        color="#17bb5f"
                        style={{ marginBottom: 13, animation: "pop-tick .33s cubic-bezier(.19,.7,.68,1.38)" }}
                    />
                    <div style={{ fontWeight: 800, fontSize: 21, marginBottom: 2 }}>{message}</div>
                    <button onClick={onClose} style={modalBtn("#17bb5f", "#fff", 17, 10)}>Close</button>
                    <style>{`
                        @keyframes pop-tick {
                            0% { opacity: 0; transform: scale(0.46);}
                            67% { opacity: 1; transform: scale(1.14);}
                            100% { opacity: 1; transform: scale(1);}
                        }
                    `}</style>
                </div>
            </div>
        );
    }

    // Custom styles for DataTable
    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #dee2e6',
            },
        },
        headCells: {
            style: {
                fontSize: '14px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
            },
        },
        cells: {
            style: {
                fontSize: '14px',
            },
        },
    };

    return (
        <section style={{ padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", marginBottom: 20 }}>
                <h1 style={{
                    display: "flex",
                    alignItems: "center", gap: 13,
                    marginBottom: 13,
                    fontSize: 38,
                    marginRight: 10,
                    color: "#215ea7",
                    verticalAlign: "middle",
                    filter: "drop-shadow(0px 1px 4px #b5beff77)",
                }}>
                    <AiOutlineEdit size={32} color="#215ea7" />
                    Contact Approaches
                </h1>
            </div>

            {/* Filters */}
            <div className="row mb-3">
                <div className="col-md-4">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search contacts..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div className="col-md-3">
                    <select
                        className="form-select"
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                    >
                        {statusOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="col-md-3">
                    <select
                        className="form-select"
                        value={selectedPriority}
                        onChange={(e) => setSelectedPriority(e.target.value)}
                    >
                        {priorityOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="col-md-2">
                    <button className="btn btn-primary w-100" onClick={fetchData}>
                        Refresh
                    </button>
                </div>
            </div>

            {/* Data Table */}
            <DataTable
                columns={columns}
                data={filteredData}
                pagination
                paginationPerPage={perPage}
                paginationRowsPerPageOptions={[10, 20, 30, 40, 50]}
                onChangeRowsPerPage={setPerPage}
                onChangePage={setCurrentPage}
                paginationDefaultPage={currentPage}
                highlightOnHover
                striped
                responsive
                persistTableHead
                progressPending={loading}
                customStyles={customStyles}
                noHeader
            />

            {/* Details Modal */}
            {showDetailsModal && selectedContact && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Contact Details</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowDetailsModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <strong>Name:</strong> {selectedContact.name}
                                    </div>
                                    <div className="col-md-6">
                                        <strong>Email:</strong> {selectedContact.email}
                                    </div>
                                </div>
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <strong>Phone:</strong> {selectedContact.phone || "Not provided"}
                                    </div>
                                    <div className="col-md-6">
                                        <strong>Subject:</strong> {selectedContact.subject || "No subject"}
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <strong>Message:</strong>
                                    <div className="border p-3 mt-2 rounded">
                                        {selectedContact.message}
                                    </div>
                                </div>
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Status</label>
                                        <select
                                            className="form-select"
                                            value={selectedContact.status}
                                            onChange={(e) => setSelectedContact({...selectedContact, status: e.target.value})}
                                        >
                                            {statusOptions.slice(1).map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Priority</label>
                                        <select
                                            className="form-select"
                                            value={selectedContact.priority}
                                            onChange={(e) => setSelectedContact({...selectedContact, priority: e.target.value})}
                                        >
                                            {priorityOptions.slice(1).map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Admin Notes</label>
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        value={adminNotes}
                                        onChange={(e) => setAdminNotes(e.target.value)}
                                        placeholder="Add admin notes..."
                                    ></textarea>
                                </div>
                                {selectedContact.admin_response && (
                                    <div className="mb-3">
                                        <strong>Previous Response:</strong>
                                        <div className="border p-3 mt-2 rounded bg-light">
                                            {selectedContact.admin_response}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowDetailsModal(false)}
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleUpdateContact}
                                    disabled={isUpdating}
                                >
                                    {isUpdating ? "Updating..." : "Update Contact"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Modal */}
            {successMsg && (
                <SuccessModal message={successMsg} onClose={closeSuccess} />
            )}

        </section>
    );
}
